import { AssertNeverError } from '@omnea-digital/toolbox'
import { z } from 'zod'
import { refOn } from '@omnea-app/packages/universal/references/refKinds'
import { SlicesConfigReadSchema, SlicesConfigSchema } from './sliceCatalogue'

// A malformed value catches to whatever the field parses `undefined` to — its declared default, or
// undefined — so the fallback can never drift from the field's own definition. The load-time parse fails
// fast on a field that can't tolerate absence, which would otherwise make the catch itself throw mid-read.
const selfHealField = <Field extends z.ZodType>(field: Field) => {
  field.parse(undefined)
  return field.catch(() => field.parse(undefined))
}

// Narrower than `z.ZodRawShape` (whose values are the core `$ZodType`, which `.catch` does not exist on).
type CapabilityShape = Record<string, z.ZodType>

type SelfHealedShape<Shape extends CapabilityShape> = { [Key in keyof Shape]: z.ZodCatch<Shape[Key]> }

const selfHealShape = <Shape extends CapabilityShape>(shape: Shape): SelfHealedShape<Shape> =>
  // Object.fromEntries erases the per-key schema types the mapped type preserves.
  Object.fromEntries(
    Object.entries(shape).map(([key, field]) => [key, selfHealField(field)])
  ) as unknown as SelfHealedShape<Shape>

// Self-healing keeps a capability usable when one field is corrupt, but a config that couldn't be read
// cleanly is not a safe basis for unattended action: a corrupt `instructions` reads as no instructions,
// which an intact autonomy block would still auto-apply. Dropping autonomy leaves the capability running
// and recommending, with a human applying the outcome.
const withAutonomySuppressed = <Config extends object>(config: Config): Config => ({ ...config, autonomy: undefined })

// Derives a capability's read artefacts from its read shape. The shape as written is the malformed-probe
// — a present field of the wrong type fails it, while missing fields and unknown-but-string slice names
// are legitimate (legacy/removed slices). The parse schema is the same shape with every field self-healed,
// so one malformed field never voids its siblings and `instructions` survives a corrupt `slices`.
const capabilityReadContract = <Shape extends CapabilityShape>(shape: Shape) => {
  const readProbe = z.object(shape)
  const readSchema = z.object(selfHealShape(shape))
  return {
    readSchema,
    parseForRead: (capabilityRaw: unknown): { config: z.infer<typeof readSchema> | undefined; malformed: boolean } => {
      const malformed = capabilityRaw != null && !readProbe.safeParse(capabilityRaw).success
      const config = readSchema.safeParse(capabilityRaw).data
      return { config: config !== undefined && malformed ? withAutonomySuppressed(config) : config, malformed }
    },
  }
}

// The review part of the agent config — `AgentConfigV2` nests exactly this shape under `review`.
export const AgenticReviewConfigSchema = z.object({
  enabled: z.boolean(),
  instructions: z.string(),
  slices: SlicesConfigSchema.optional().default({}),
  autonomy: z
    .object({
      approval: z.object({ enabled: z.boolean().default(false) }),
      rejection: z.object({ enabled: z.boolean().default(false) }),
    })
    .optional(),
  // Provenance for a config installed from a marketplace template; install is a copy, so nothing
  // branches on it and the org is free to edit the config afterwards.
  // oxlint-disable-next-line omnea-internal/declare-config-references -- points at the org-less marketplace catalogue, not a deletable tenant row
  sourceTemplateId: z.uuid().optional(),
})
export type AgenticReviewConfig = z.infer<typeof AgenticReviewConfigSchema>

// Read side: every field optional, because persisted configs may predate any of them.
const reviewContract = capabilityReadContract({
  enabled: z.boolean().optional(),
  instructions: z.string().optional(),
  slices: SlicesConfigReadSchema.optional().default({}),
  // The legacy top-level policy list, read-side only. It stays string-only because uuid validation is
  // write-side and a bad id fails loud in `loadPolicies`.
  // oxlint-disable-next-line omnea-internal/declare-config-references -- the walk runs over the write schema, which declares this; a ref here would be read by nothing
  policyIds: z.array(z.string()).optional().default([]),
  // Tolerates a one-sided block (a missing `approval`/`rejection`) where the write schema requires both,
  // but stays strict on the `enabled` type so a malformed side trips the malformed probe and the whole
  // block catches to `undefined` — the gate then fails safe to human review rather than honouring half
  // of a corrupt block.
  autonomy: z
    .object({
      approval: z.object({ enabled: z.boolean().optional() }).optional(),
      rejection: z.object({ enabled: z.boolean().optional() }).optional(),
    })
    .optional(),
})

// The original flat agent config stored this shape at the top level of `payload.agentConfig`, carrying
// the policy list top-level (V2 stores it only on the `slices.policies` entry). Persisted workflows
// still hold it; the UI only writes `AgentConfigV2`, so it survives read-side only.
export const LegacyAgentConfigSchema = z.object({
  ...AgenticReviewConfigSchema.shape,
  policyIds: z.array(refOn(z.uuid(), 'policy')).optional(),
})
export type LegacyAgentConfig = z.infer<typeof LegacyAgentConfigSchema>

export const AgenticFillInConfigSchema = z.object({
  enabled: z.boolean(),
  instructions: z.string(),
  slices: SlicesConfigSchema.optional().default({}),
  // Autonomous completion: the agent marks the filled task complete rather than leaving it for a human.
  autonomy: z.object({ enabled: z.boolean().default(false) }).optional(),
  // Provenance for a config installed from a marketplace template; install is a copy, so nothing
  // branches on it and the org is free to edit the config afterwards.
  // oxlint-disable-next-line omnea-internal/declare-config-references -- points at the org-less marketplace catalogue, not a deletable tenant row
  sourceTemplateId: z.uuid().optional(),
})
export type AgenticFillInConfig = z.infer<typeof AgenticFillInConfigSchema>

const fillInContract = capabilityReadContract({
  enabled: z.boolean().optional(),
  instructions: z.string().optional(),
  slices: SlicesConfigReadSchema.optional().default({}),
  autonomy: z.object({ enabled: z.boolean().optional() }).optional(),
})

export const AgenticContentGenerationConfigSchema = z.object({
  enabled: z.boolean(),
  instructions: z.string(),
  slices: SlicesConfigSchema.optional().default({}),
  // Whether the rendered document carries the organisation's own logo in its page header. Optional
  // with no default so configs from before this field existed stay valid as literals; absent is off.
  includeLogo: z.boolean().optional(),
  // Autonomous completion: the agent completes the task with its drafted document rather than leaving the
  // draft for a human.
  autonomy: z.object({ enabled: z.boolean().default(false) }).optional(),
  // Provenance for a config installed from a marketplace template; install is a copy, so nothing
  // branches on it and the org is free to edit the config afterwards.
  // oxlint-disable-next-line omnea-internal/declare-config-references -- points at the org-less marketplace catalogue, not a deletable tenant row
  sourceTemplateId: z.uuid().optional(),
})
export type AgenticContentGenerationConfig = z.infer<typeof AgenticContentGenerationConfigSchema>

const contentGenerationContract = capabilityReadContract({
  enabled: z.boolean().optional(),
  instructions: z.string().optional(),
  slices: SlicesConfigReadSchema.optional().default({}),
  includeLogo: z.boolean().optional().default(false),
  autonomy: z.object({ enabled: z.boolean().optional() }).optional(),
})

// Every capability an `AgentConfigV2` can carry. The classification probes (`isAgentConfigV2`,
// `isRawAgentConfigV2`, the schema refine), the V2 object shape (via `satisfies`) and the rollback
// disablers all key off this union, so a capability missing from any of them is a compile error.
export const AGENT_CAPABILITY_KEYS = ['review', 'fillIn', 'contentGeneration'] as const
export type AgentCapabilityKey = (typeof AGENT_CAPABILITY_KEYS)[number]

// Every part is optional so a config can carry only the sides it uses. The builder currently writes every
// side, with `enabled: false` for an unused one. At least one part must be present: an empty object would
// otherwise validate here yet be classified as legacy by `isAgentConfigV2`.
export const AgentConfigV2Schema = z
  .object({
    review: AgenticReviewConfigSchema.optional(),
    fillIn: AgenticFillInConfigSchema.optional(),
    contentGeneration: AgenticContentGenerationConfigSchema.optional(),
  } satisfies Record<AgentCapabilityKey, z.ZodType>)
  .refine((config) => AGENT_CAPABILITY_KEYS.some((key) => config[key] !== undefined), {
    message: 'At least one capability must be configured',
  })

export type AgentConfigV2 = z.infer<typeof AgentConfigV2Schema>

// Marketplace template classification. Single-capability types today; combined types may be
// introduced later.
export const AgentTemplateTypeSchema = z.enum(['FILL_IN', 'REVIEW', 'CONTENT_GENERATION'])
export type AgentTemplateType = z.infer<typeof AgentTemplateTypeSchema>

// What `payload.agentConfig` can hold on a block config: either shape is valid on read; writers emit V2.
export type AgentConfig = LegacyAgentConfig | AgentConfigV2

export const isAgentConfigV2 = (config: AgentConfig): config is AgentConfigV2 =>
  AGENT_CAPABILITY_KEYS.some((key) => key in config)

// V2 configs nest each capability under its key. Detected structurally so a raw jsonb blob doesn't need
// to be typed before unwrapping.
const isRawAgentConfigV2 = (raw: unknown): raw is Partial<Record<AgentCapabilityKey, unknown>> =>
  typeof raw === 'object' && raw !== null && AGENT_CAPABILITY_KEYS.some((key) => key in raw)

// Reads one capability off a config, failing safe to undefined on null/non-object/legacy jsonb rather
// than throwing on the `in` probe.
const getAgentCapabilityConfig = <Key extends AgentCapabilityKey>(
  config: AgentConfig | null | undefined,
  key: Key
): AgentConfigV2[Key] => (config != null && typeof config === 'object' && isAgentConfigV2(config) ? config[key] : undefined)

// Reading a legacy flat config folds its top-level policy list into the `slices.policies` entry (a
// present entry is authoritative), so callers always get the V2 review shape — and saving a config read
// through here migrates it. The legacy fallback is review-only: every other capability was born V2.
export const getAgenticReviewConfig = (config: AgentConfig | null | undefined): AgenticReviewConfig | undefined => {
  // Read from unvalidated persisted jsonb: fail safe on null/non-object rather than throw on the `in` probe.
  if (config == null || typeof config !== 'object') return undefined
  if (isAgentConfigV2(config)) return config.review
  const { policyIds = [], ...review } = config
  if (review.slices?.policies || policyIds.length === 0) return review
  return { ...review, slices: { ...review.slices, policies: { enabled: true, policyIds } } }
}

export const getAgenticFillInConfig = (config: AgentConfig | null | undefined): AgenticFillInConfig | undefined =>
  getAgentCapabilityConfig(config, 'fillIn')

export const getAgenticContentGenerationConfig = (
  config: AgentConfig | null | undefined
): AgenticContentGenerationConfig | undefined => getAgentCapabilityConfig(config, 'contentGeneration')

// The capabilities a config switches on. Ask this rather than or-ing the individual getters: a gate that
// names its capabilities goes stale the moment one is added.
export const getEnabledAgentCapabilities = (config: AgentConfig | null | undefined): AgentCapabilityKey[] => {
  if (config == null || typeof config !== 'object') return []
  // A legacy flat config is its own review config, and carries no other capability.
  if (!isAgentConfigV2(config)) return config.enabled === true ? ['review'] : []
  return AGENT_CAPABILITY_KEYS.filter((key) => config[key]?.enabled === true)
}

const disableApprovalAutonomy = (autonomy: AgenticReviewConfig['autonomy']): NonNullable<AgenticReviewConfig['autonomy']> => ({
  approval: { enabled: false },
  rejection: autonomy?.rejection ?? { enabled: false },
})

// Review keeps its rejection autonomy: that alone doesn't auto-complete the step. Don't refactor to a
// `(key, capability)` parameter — it type-checks against the union, so the branches stop being safe.
const withCapabilityAutonomyDisabled = (config: AgentConfigV2, key: AgentCapabilityKey): AgentConfigV2 => {
  switch (key) {
    case 'review':
      return config.review === undefined
        ? config
        : { ...config, review: { ...config.review, autonomy: disableApprovalAutonomy(config.review.autonomy) } }
    case 'fillIn':
      return config.fillIn === undefined ? config : { ...config, fillIn: { ...config.fillIn, autonomy: { enabled: false } } }
    case 'contentGeneration':
      return config.contentGeneration === undefined
        ? config
        : { ...config, contentGeneration: { ...config.contentGeneration, autonomy: { enabled: false } } }
    default:
      throw new AssertNeverError(key)
  }
}

// Reads what withCapabilityAutonomyDisabled writes, so the pre-rollback warning names exactly what
// rollback switches off. The pairing is asserted per capability in the tests, not by the types.
export const hasAutonomyDisabledByRollback = (config: AgentConfig | null | undefined): boolean => {
  // Read from unvalidated persisted jsonb: fail safe on null/non-object rather than throw on the `in` probe.
  if (config == null || typeof config !== 'object') return false
  if (!isAgentConfigV2(config)) return config.autonomy?.approval?.enabled === true
  return (
    config.review?.autonomy?.approval?.enabled === true ||
    config.fillIn?.autonomy?.enabled === true ||
    config.contentGeneration?.autonomy?.enabled === true
  )
}

// Used on rollback: every autonomy that would complete the step on the agent's own is switched off, so a
// human must apply the decision and complete the tasks. The agent still recommends and still fills forms in.
export const withAutonomyDisabledForRollback = (config: AgentConfig): AgentConfig => {
  if (!isAgentConfigV2(config)) {
    return { ...config, autonomy: disableApprovalAutonomy(config.autonomy) }
  }
  return AGENT_CAPABILITY_KEYS.reduce<AgentConfigV2>(withCapabilityAutonomyDisabled, config)
}

export const AgenticReviewConfigReadSchema = reviewContract.readSchema
export type AgenticReviewConfigRead = z.infer<typeof AgenticReviewConfigReadSchema>

// Accepts both the legacy flat shape and V2; always returns the review config.
export const parseAgenticReviewConfigForRead = (
  raw: unknown
): { config: AgenticReviewConfigRead | undefined; malformed: boolean } =>
  reviewContract.parseForRead(isRawAgentConfigV2(raw) ? raw.review : raw)

export const AgenticFillInConfigReadSchema = fillInContract.readSchema
export type AgenticFillInConfigRead = z.infer<typeof AgenticFillInConfigReadSchema>

// A legacy flat config has no fill-in part, so it parses to `undefined` (never malformed) rather than
// being misread as fill-in config.
export const parseAgenticFillInConfigForRead = (
  raw: unknown
): { config: AgenticFillInConfigRead | undefined; malformed: boolean } =>
  fillInContract.parseForRead(isRawAgentConfigV2(raw) ? raw.fillIn : undefined)

export const AgenticContentGenerationConfigReadSchema = contentGenerationContract.readSchema
export type AgenticContentGenerationConfigRead = z.infer<typeof AgenticContentGenerationConfigReadSchema>

// Born V2 like fill-in: a legacy flat config has no content generation part.
export const parseAgenticContentGenerationConfigForRead = (
  raw: unknown
): { config: AgenticContentGenerationConfigRead | undefined; malformed: boolean } =>
  contentGenerationContract.parseForRead(isRawAgentConfigV2(raw) ? raw.contentGeneration : undefined)

// The cast target for `step.input.metadata.config` in task-agent code: label + agentConfig, the two
// fields every agentic block type (APPROVAL, BUDGET_APPROVAL) is guaranteed to carry. Keep it to
// universally-present fields — never widen it with block-specific ones.
export interface AgenticBlockConfig {
  payload: {
    label: string
    agentConfig?: AgentConfig
  }
}
